import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
	type GitHubReturn,
	handOffGitHubReturn,
} from "@/components/admin/github-installation-picker";
import { OrganizationApps } from "@/components/admin/organization-apps";
import { BasePageContainer } from "@/components/base";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute("/_authenticated/$orgSlug/admin/apps")({
	component: AdminAppsPage,
	// GitHub's sign-in/install parameters, forwarded here untouched by the anonymous callback
	// (git-provider-connection-callback-controller.cs). This signed-in page — not the callback —
	// lists the installations and connects the one the admin picks (SEC-002 F1). This page is
	// normally the window the connect dialog opened GitHub in; it hands the parameters back to that
	// dialog and closes itself.
	validateSearch: (search: Record<string, unknown>): GitHubInstallSearch => ({
		installation_id: optionalString(search.installation_id),
		setup_action: optionalString(search.setup_action),
		state: optionalString(search.state),
		code: optionalString(search.code),
	}),
});

type GitHubInstallSearch = {
	installation_id?: string;
	setup_action?: string;
	state?: string;
	code?: string;
};

function optionalString(value: unknown): string | undefined {
	// TanStack parses numeric-looking params (installation_id) into numbers.
	return typeof value === "string" || typeof value === "number"
		? String(value)
		: undefined;
}

function AdminAppsPage() {
	const navigate = useNavigate();
	const install = Route.useSearch();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const [githubReturn, setGitHubReturn] = useState<GitHubReturn>();
	const handledInstallState = useRef<string | undefined>(undefined);

	useEffect(() => {
		setTitle("Apps");
		setActions([]);
		return () => setActions([]);
	}, [setTitle, setActions]);

	// Coming back from GitHub: clear the one-time params from the URL right away, then hand them to
	// the connect dialog that opened this window. With no dialog listening (the link was opened some
	// other way), show the picker here. The ref keeps StrictMode's double effect from handing off twice.
	useEffect(() => {
		const { installation_id, setup_action, state, code } = install;
		if (!state || handledInstallState.current === state) return;
		handledInstallState.current = state;
		navigate({ to: ".", search: {}, replace: true });

		const ret: GitHubReturn = {
			state,
			code,
			installationId: installation_id,
			setupAction: setup_action,
		};
		handOffGitHubReturn(ret).then((handed) =>
			handed ? window.close() : setGitHubReturn(ret),
		);
	}, [install, navigate]);

	return (
		<BasePageContainer>
			<OrganizationApps githubReturn={githubReturn} />
		</BasePageContainer>
	);
}
